import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { format, formatISO } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { listAuditLog } from '@/api/endpoints';
import type { AuditFilters, AuditLogEntry, Page } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

interface FilterState {
  action: string;
  actor_user_id: string;
  target_type: string;
  since: string;
  until: string;
}

const EMPTY: FilterState = { action: '', actor_user_id: '', target_type: '', since: '', until: '' };

function filtersToQuery(state: FilterState, limit: number, offset: number): AuditFilters {
  const out: AuditFilters = { limit, offset };
  if (state.action.trim()) out.action = state.action.trim();
  if (state.target_type.trim()) out.target_type = state.target_type.trim();
  if (state.actor_user_id.trim()) {
    const id = Number(state.actor_user_id.trim());
    if (Number.isFinite(id)) out.actor_user_id = id;
  }
  if (state.since) {
    try {
      out.since = formatISO(new Date(state.since));
    } catch {
      // ignore
    }
  }
  if (state.until) {
    try {
      out.until = formatISO(new Date(state.until));
    } catch {
      // ignore
    }
  }
  return out;
}

export function AdminAuditPage() {
  const [filters, setFilters] = useState<FilterState>(EMPTY);
  const [applied, setApplied] = useState<FilterState>(EMPTY);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const query = useMemo(
    () => filtersToQuery(applied, pageSize, page * pageSize),
    [applied, page, pageSize],
  );

  const audit = useQuery<Page<AuditLogEntry>>({
    queryKey: ['admin', 'audit', query],
    queryFn: () => listAuditLog(query),
  });

  const apply = () => {
    setPage(0);
    setApplied(filters);
  };
  const reset = () => {
    setFilters(EMPTY);
    setApplied(EMPTY);
    setPage(0);
  };

  return (
    <Container maxWidth="lg" disableGutters>
      <PageHeader
        title="Audit log"
        subtitle="User-configured actions across the system, newest first."
      />

      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Action"
              placeholder="e.g. rule.create"
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Target type"
              placeholder="e.g. sync_rule"
              value={filters.target_type}
              onChange={(e) => setFilters({ ...filters, target_type: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Actor user id"
              type="number"
              value={filters.actor_user_id}
              onChange={(e) => setFilters({ ...filters, actor_user_id: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Since"
              type="datetime-local"
              slotProps={{ inputLabel: { shrink: true } }}
              value={filters.since}
              onChange={(e) => setFilters({ ...filters, since: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Until"
              type="datetime-local"
              slotProps={{ inputLabel: { shrink: true } }}
              value={filters.until}
              onChange={(e) => setFilters({ ...filters, until: e.target.value })}
            />
          </Grid>
          <Grid size={12}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={reset}>Reset</Button>
              <Button variant="contained" onClick={apply}>
                Apply filters
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {audit.isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {audit.isSuccess && (
        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Metadata</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audit.data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No matching entries.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {audit.data.items.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(row.created_at), 'PP HH:mm:ss')}
                  </TableCell>
                  <TableCell>{row.actor_user_id ?? '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={row.action} />
                  </TableCell>
                  <TableCell>
                    {row.target_type}
                    {row.target_id ? `#${row.target_id}` : ''}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Box
                      component="code"
                      sx={{
                        display: 'block',
                        fontSize: 12,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 80,
                        color: 'text.secondary',
                      }}
                    >
                      {row.metadata ? JSON.stringify(row.metadata, null, 0) : '—'}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={audit.data.total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        </TableContainer>
      )}
    </Container>
  );
}
